import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Sidebar from "./components/Layout/Sidebar";
import Header from "./components/Layout/Header";
import Login from "./components/Auth/Login";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import Dashboard from "./components/Dashboard/Dashboard";
import LibraryManagement from "./components/Library/LibraryManagement";
import TransportationSystem from "./components/Transportation/TransportationSystem";
import CafeteriaSystem from "./components/Cafeteria/CafeteriaSystem";
import ELibrary from "./components/ELibrary/ELibrary";
import AdminDashboard from "./components/Admin/AdminDashboard";
// Student components
import BookRequests from "./components/Student/BookRequests";
import CafeteriaPurchase from "./components/Student/CafeteriaPurchase";
import StudentDownloads from "./components/Student/StudentDownloads";
// Parent components
import ChildMonitoring from "./components/Parent/ChildMonitoring";
import CafeteriaControls from "./components/Parent/CafeteriaControls";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./App.css";

function AppContent() {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading GISS Library Management System...</p>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="app">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={`main-content ${sidebarOpen ? "sidebar-open" : ""}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />

            {/* Dashboard - All users */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredPermissions={["dashboard"]}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Library Routes */}
            <Route
              path="/library"
              element={
                <ProtectedRoute requiredPermissions={["library_manage"]}>
                  <LibraryManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/requests"
              element={
                <ProtectedRoute requiredPermissions={["library_request"]}>
                  <BookRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library/borrow"
              element={
                <ProtectedRoute requiredPermissions={["library_borrow"]}>
                  <LibraryManagement />
                </ProtectedRoute>
              }
            />

            {/* Transportation Routes */}
            <Route
              path="/transportation"
              element={
                <ProtectedRoute requiredPermissions={["transportation_manage"]}>
                  <TransportationSystem />
                </ProtectedRoute>
              }
            />

            {/* Cafeteria Routes */}
            <Route
              path="/cafeteria"
              element={
                <ProtectedRoute requiredPermissions={["cafeteria_manage"]}>
                  <CafeteriaSystem />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cafeteria/purchase"
              element={
                <ProtectedRoute requiredPermissions={["cafeteria_purchase"]}>
                  <CafeteriaPurchase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cafeteria/controls"
              element={
                <ProtectedRoute requiredPermissions={["cafeteria_controls"]}>
                  <CafeteriaControls />
                </ProtectedRoute>
              }
            />

            {/* E-Library Routes */}
            <Route
              path="/e-library"
              element={
                <ProtectedRoute requiredPermissions={["e_library_access"]}>
                  <ELibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/e-library/downloads"
              element={
                <ProtectedRoute requiredPermissions={["e_library_download"]}>
                  <StudentDownloads />
                </ProtectedRoute>
              }
            />

            {/* Parent Routes */}
            <Route
              path="/child-monitoring"
              element={
                <ProtectedRoute requiredPermissions={["library_view_child"]}>
                  <ChildMonitoring />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredPermissions={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>
      </div>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
